import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-[#E5E7EB] p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-6 w-6 text-[#2563EB]" />
          <span className="font-heading font-extrabold text-lg">AccuSearch WPM</span>
        </div>
        {!token && (
          <div data-testid="reset-password-invalid">
            <h1 className="font-heading text-xl font-extrabold text-[#111827] mb-2">Invalid link</h1>
            <p className="text-sm text-[#4B5563]">This reset link is missing its token. Please request a new one.</p>
          </div>
        )}
        {token && done && (
          <div data-testid="reset-password-success">
            <CheckCircle2 className="h-10 w-10 text-[#10B981] mb-4" />
            <h1 className="font-heading text-xl font-extrabold text-[#111827] mb-2">Password updated</h1>
            <p className="text-sm text-[#4B5563]">Redirecting you to sign in...</p>
          </div>
        )}
        {token && !done && (
          <form onSubmit={submit} data-testid="reset-password-form">
            <h1 className="font-heading text-xl font-extrabold text-[#111827] mb-1">Set a new password</h1>
            <p className="text-sm text-[#4B5563] mb-6">Minimum 6 characters.</p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="new-pw" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">New Password</Label>
                <Input id="new-pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 rounded-sm" data-testid="reset-password-new-input" />
              </div>
              <div>
                <Label htmlFor="confirm-pw" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Confirm Password</Label>
                <Input id="confirm-pw" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 rounded-sm" data-testid="reset-password-confirm-input" />
              </div>
              {error && <div className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2" data-testid="reset-password-error">{error}</div>}
              <Button type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
                data-testid="reset-password-submit-button">
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </div>
          </form>
        )}
        <Link to="/login" className="mt-6 inline-block text-xs font-semibold text-[#2563EB] hover:underline" data-testid="reset-back-to-login-link">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
